import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMobileAlt,
  faCommentDots,
  faEye,
  faUserCheck,
  faTriangleExclamation,
  faExternalLinkAlt,
  faClock,
  faRotateRight,
  faTag,
  faLayerGroup,
  faExchangeAlt,
  faCodeBranch
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import { formatRelativeTime, formatWaitingTime, getTicketInitials, getTicketStatusInfo, getTicketUrgencyInfo } from '../utils';
import { formatWhatsAppText } from '../../TicketDetailView/utils';

const TicketCardItem = ({
  ticket,
  onSelectTicket,
  onAssignTicketAgent,
  agents = []
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const [, setCurrentTime] = useState(() => Date.now());

  // Actualizar cada 60 segundos para que el tiempo en pantalla avance en vivo sin recargar
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  const ticketId = ticket.idTicket || ticket.id;
  const ticketUrl = `/support-engine/ticket/${ticketId}`;
  const clientName = ticket.vCustomerName?.trim() || 'Cliente WhatsApp';
  const phone = ticket.vTelefono || 'Sin teléfono';
  const lastTime = formatRelativeTime(ticket.dUltimaActividad || ticket.dCreatedDate);
  const isPending = Number(ticket.iEstadoTicket || ticket.status) === 1;
  const isResolved = Number(ticket.iEstadoTicket || ticket.status) === 2;

  // Para pendientes: tiempo de espera sin contestar (desde la creación)
  // Para en progreso: tiempo de atención (desde que se tomó/respondió con dFechaRespuestaInicial)
  const startDate = isPending
    ? (ticket.dCreatedDate || ticket.createdDate)
    : (ticket.dFechaRespuestaInicial || ticket.dCreatedDate || ticket.createdDate);

  const { timeFormatted, timeClass } = getTicketUrgencyInfo(
    startDate,
    ticket.iEstadoTicket || ticket.status
  );

  const hasResponded = ticket.iTiempoRespuestaMinutos != null || ticket.dFechaRespuestaInicial != null;
  const waitingTime = !hasResponded ? formatWaitingTime(ticket.dCreatedDate) : null;
  const statusInfo = getTicketStatusInfo(ticket.iEstadoTicket || ticket.status);
  const initials = getTicketInitials(clientName, phone);

  // Determinar el asesor asignado
  const hasAssignedAgent = Boolean(ticket.idUsuarioAsignado && Number(ticket.idUsuarioAsignado) > 0);
  const assignedAgent = hasAssignedAgent ? agents?.find(a => Number(a.idUsuario) === Number(ticket.idUsuarioAsignado)) : null;
  const rawAgentName = (assignedAgent?.vNombreCompleto || assignedAgent?.vNombre || ticket.vNombreUsuarioAsignado || '').trim();
  const assignedAgentName = hasAssignedAgent && rawAgentName ? rawAgentName : null;

  const handleCardClick = e => {
    if (e.target.closest('button') || e.target.closest('a')) {
      return;
    }
    onSelectTicket(ticket);
  };

  const handleCardAuxClick = e => {
    if (e.button === 1) { // Middle click
      if (e.target.closest('button') || e.target.closest('a')) {
        return;
      }
      window.open(ticketUrl, '_blank');
    }
  };

  // Solo mostrar estilo verde de mensaje sin leer en tickets en progreso o resueltos, nunca en la cola de pendientes
  const isUnread = Boolean(ticket.bSinLeer) && !isPending;

  // Adaptación de colores de estado para modo oscuro
  const statusBg = isDark
    ? (isPending
      ? 'rgba(245, 158, 11, 0.18)'
      : Number(ticket.iEstadoTicket) === 3
        ? 'rgba(14, 165, 233, 0.18)'
        : 'rgba(16, 185, 129, 0.18)')
    : statusInfo.bgColor;

  const statusColor = isDark
    ? (isPending
      ? '#fbbf24'
      : Number(ticket.iEstadoTicket) === 3
        ? '#38bdf8'
        : '#34d399')
    : statusInfo.textColor;

  return (
    <div
      onClick={handleCardClick}
      onAuxClick={handleCardAuxClick}
      className="border rounded-3 transition-base cursor-pointer hover-shadow-sm position-relative"
      style={{
        borderLeft: isUnread ? '4px solid #25d366' : `4px solid ${statusInfo.accentColor}`,
        backgroundColor: isDark ? (isUnread ? '#07241c' : '#121e2d') : (isUnread ? '#f8fdf9' : '#ffffff'),
        borderColor: isDark ? (isUnread ? '#059669' : '#233142') : (isUnread ? '#a7f3d0' : '#eef2f6'),
        boxShadow: isUnread ? '0 2px 10px rgba(37, 211, 102, 0.12)' : 'none',
        cursor: 'pointer',
        zIndex: 1,
        overflow: 'visible'
      }}
    >
      <div className="d-flex p-3 align-items-center flex-wrap flex-md-nowrap gap-3">
        {/* 1. Left Avatar & Status Col */}
        <div
          className="d-flex flex-column align-items-center justify-content-center flex-shrink-0"
          style={{ width: '90px' }}
        >
          <div className="position-relative mb-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center shadow-xs"
              style={{
                width: '42px',
                height: '42px',
                backgroundColor: isUnread ? (isDark ? '#064e3b' : '#ecfdf5') : statusBg,
                color: isUnread ? (isDark ? '#34d399' : '#059669') : statusColor,
                fontWeight: 700,
                fontSize: '0.9rem',
                border: isUnread ? '2px solid #34d399' : (isDark ? '1px solid #233142' : 'none')
              }}
            >
              {initials}
            </div>
            {isUnread && (
              <span
                className="position-absolute top-0 start-100 translate-middle rounded-circle border border-2"
                style={{
                  backgroundColor: '#25d366',
                  borderColor: isDark ? '#121e2d' : '#ffffff',
                  width: '13px',
                  height: '13px',
                  boxShadow: '0 0 6px rgba(37, 211, 102, 0.8)'
                }}
                title="Mensaje nuevo sin leer"
              />
            )}
          </div>

          <div className="d-flex align-items-center gap-1">
            <span
              className="px-2 py-1 rounded"
              style={{
                backgroundColor: statusBg,
                color: statusColor,
                fontSize: '0.7rem',
                fontWeight: 600
              }}
            >
              {statusInfo.label}
            </span>
            {statusInfo.hasWarning && (
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                style={{ color: '#ef4444', fontSize: '0.8rem' }}
              />
            )}
          </div>
        </div>

        {/* 2. Center Content Col */}
        <div className="flex-grow-1 min-w-0 ps-md-2">
          {/* Contact Name & Phone */}
          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
            {ticket.idClienteAkasia ? (
              <a
                href={`/clientes/detalle/${ticket.idClienteAkasia}`}
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}
                className="fw-bold text-decoration-none d-inline-flex align-items-center gap-1"
                style={{ fontSize: '0.98rem', color: isDark ? '#38bdf8' : '#0284c7' }}
                title={`Ver perfil del cliente #${ticket.idClienteAkasia}`}
              >
                <span style={{ color: isDark ? '#f9fafd' : '#0f172a' }}>{clientName}</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.7rem', opacity: 0.7 }} />
              </a>
            ) : (
              <span className="fw-bold" style={{ fontSize: '0.98rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
                {clientName}
              </span>
            )}

            <span
              className="badge px-2 py-1"
              style={{
                fontSize: '0.74rem',
                fontWeight: 600,
                backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                color: isDark ? '#94a3b8' : '#64748b',
                border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                borderRadius: '6px'
              }}
              title={`ID del Ticket: #${ticketId}`}
            >
              #TCK-{ticketId}
            </span>

            {/* Badge de Ticket Secundario Combinado */}
            {Boolean(ticket.idTicketDestino) && (
              <span
                className="badge rounded-pill d-inline-flex align-items-center gap-1"
                style={{
                  backgroundColor: isDark ? 'rgba(99, 102, 241, 0.18)' : '#e0e7ff',
                  color: isDark ? '#818cf8' : '#4338ca',
                  border: isDark ? '1px solid rgba(99, 102, 241, 0.35)' : '1px solid #c7d2fe',
                  fontSize: '0.70rem',
                  fontWeight: 700,
                  padding: '3px 8px',
                  cursor: 'pointer'
                }}
                title={`Ticket combinado en Ticket Principal #${ticket.idTicketDestino}. Clic para abrir en nueva pestaña.`}
                onClick={e => {
                  e.stopPropagation();
                  window.open(`/support-engine/ticket/${ticket.idTicketDestino}`, '_blank');
                }}
              >
                <FontAwesomeIcon icon={faExchangeAlt} style={{ fontSize: '0.62rem' }} />
                Combinado con → ticket #{ticket.idTicketDestino}
              </span>
            )}

            {/* Badge de Ticket Principal con tickets combinados */}
            {Boolean(ticket.vTicketsCombinados) && (
              <span
                className="badge rounded-pill d-inline-flex align-items-center gap-1"
                style={{
                  backgroundColor: isDark ? 'rgba(168, 85, 247, 0.18)' : '#f3e8ff',
                  color: isDark ? '#c084fc' : '#7e22ce',
                  border: isDark ? '1px solid rgba(168, 85, 247, 0.35)' : '1px solid #e9d5ff',
                  fontSize: '0.70rem',
                  fontWeight: 700,
                  padding: '3px 8px'
                }}
                title={`Ticket Principal con tickets secundarios combinados: ${ticket.vTicketsCombinados.split(',').map(s => '#' + s.trim()).join(', ')}`}
              >
                <FontAwesomeIcon icon={faCodeBranch} style={{ fontSize: '0.62rem' }} />
                Principal (Combinado con → ticket {ticket.vTicketsCombinados.split(',').map(s => '#' + s.trim()).join(', ')})
              </span>
            )}

            {/* Badge de Ticket Reabierto */}
            {Boolean(ticket.bReabierto || ticket.isReopened) && (
              <span
                className="badge rounded-pill d-inline-flex align-items-center gap-1"
                style={{
                  backgroundColor: isDark ? 'rgba(236, 72, 153, 0.18)' : '#fdf2f8',
                  color: isDark ? '#f472b6' : '#db2777',
                  border: isDark ? '1px solid rgba(236, 72, 153, 0.35)' : '1px solid #fbcfe8',
                  fontSize: '0.70rem',
                  fontWeight: 700,
                  padding: '3px 8px'
                }}
                title="Este ticket fue reabierto tras haber sido cerrado previamente"
              >
                <FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '0.65rem' }} />
                Reabierto
              </span>
            )}

            {/* Badge estilo WhatsApp indicando nuevo mensaje / sin leer */}
            {isUnread && (
              <span
                className="badge rounded-pill d-inline-flex align-items-center gap-1"
                style={{
                  backgroundColor: '#25d366',
                  color: '#ffffff',
                  fontSize: '0.70rem',
                  fontWeight: 700,
                  padding: '3px 8px',
                  boxShadow: '0 2px 6px rgba(37, 211, 102, 0.35)',
                  letterSpacing: '0.3px'
                }}
              >
                <span
                  className="rounded-circle bg-white"
                  style={{ width: '6px', height: '6px', display: 'inline-block' }}
                />
                Nuevo mensaje
              </span>
            )}

            {/* Badges de Incidencias / Etiquetas */}
            {ticket.vIncidencias && ticket.vIncidencias.split(',').map((incidencia, idx) => {
              const label = incidencia.trim();
              if (!label) return null;
              return (
                <span
                  key={idx}
                  className="badge rounded-pill d-inline-flex align-items-center gap-1"
                  style={{
                    backgroundColor: isDark ? 'rgba(139, 92, 246, 0.18)' : '#f5f3ff',
                    color: isDark ? '#c4b5fd' : '#7c3aed',
                    border: isDark ? '1px solid rgba(139, 92, 246, 0.35)' : '1px solid #ddd6fe',
                    fontSize: '0.70rem',
                    fontWeight: 600,
                    padding: '3px 8px'
                  }}
                  title={`Incidencia: ${label}`}
                >
                  <FontAwesomeIcon icon={faTag} style={{ fontSize: '0.62rem' }} />
                  {label}
                </span>
              );
            })}

            {/* Badge de Nivel de Soporte */}
            {ticket.idCatNivelSoporte ? (
              <span
                className="badge rounded-pill d-inline-flex align-items-center gap-1"
                style={{
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.18)' : '#eff6ff',
                  color: isDark ? '#93c5fd' : '#2563eb',
                  border: isDark ? '1px solid rgba(59, 130, 246, 0.35)' : '1px solid #bfdbfe',
                  fontSize: '0.70rem',
                  fontWeight: 600,
                  padding: '3px 8px'
                }}
                title={ticket.vNivelSoporte ? `Nivel: ${ticket.vNivelSoporte}` : `Nivel de Soporte: Nivel ${ticket.idCatNivelSoporte}`}
              >
                <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '0.62rem' }} />
                {`Nivel ${ticket.idCatNivelSoporte}`}
              </span>
            ) : (
              <span
                className="badge rounded-pill d-inline-flex align-items-center gap-1"
                style={{
                  backgroundColor: isDark ? 'rgba(148, 163, 184, 0.12)' : '#f1f5f9',
                  color: isDark ? '#94a3b8' : '#64748b',
                  border: isDark ? '1px solid rgba(148, 163, 184, 0.25)' : '1px solid #e2e8f0',
                  fontSize: '0.70rem',
                  fontWeight: 600,
                  padding: '3px 8px'
                }}
                title="Sin nivel de soporte asignado"
              >
                <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: '0.62rem' }} />
                Sin nivel
              </span>
            )}

            <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: '0.72rem' }} />
              <span>{phone}</span>
            </div>
          </div>

          {/* Last Message Indicator & Tiempo de Atención */}
          <div className="d-flex align-items-center gap-3 mb-1 flex-wrap" style={{ fontSize: '0.76rem', color: isDark ? '#94a3b8' : '#64748b' }}>
            <div className="d-flex align-items-center gap-1">
              <FontAwesomeIcon icon={faCommentDots} style={{ color: isUnread ? '#25d366' : '#10b981' }} />
              <span className={isUnread ? 'fw-bold text-success' : ''}>
                Última actividad: {lastTime}
              </span>
            </div>

            {/* Indicador de tiempo según estado: Sin contestar (Pendientes) o Tiempo de atención (En Progreso) */}
            {timeFormatted && !isResolved && (
              <div
                className="d-flex align-items-center gap-1 fw-medium"
                style={{
                  color: isPending
                    ? (isDark ? '#fb923c' : '#ea580c')
                    : timeClass === 'danger'
                      ? (isDark ? '#f87171' : '#dc2626')
                      : timeClass === 'warning'
                        ? (isDark ? '#fbbf24' : '#d97706')
                        : (isDark ? '#38bdf8' : '#0284c7')
                }}
                title={isPending ? `Ticket sin contestar desde: ${startDate}` : `Ticket en atención desde: ${startDate}`}
              >
                <FontAwesomeIcon
                  icon={timeClass === 'danger' ? faTriangleExclamation : faClock}
                  style={{ fontSize: '0.70rem' }}
                />
                <span>
                  {isPending ? 'Sin contestar: ' : 'Tiempo de atención: '}
                  <strong>{timeFormatted}</strong>
                </span>
              </div>
            )}
          </div>

          {/* Último Mensaje / Asunto */}
          <p className="mb-0 lh-sm text-truncate" style={{ fontSize: '0.86rem', maxWidth: '650px' }}>
            {ticket.vAsunto && (
              <span
                className="badge border me-2"
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                  color: isDark ? '#38bdf8' : '#0284c7',
                  borderColor: isDark ? '#334155' : '#e2e8f0'
                }}
                title="Asunto / Motivo"
              >
                {ticket.vAsunto}
              </span>
            )}
            <span
              style={{
                color: isUnread
                  ? (isDark ? '#f9fafd' : '#0f172a')
                  : ticket.vUltimoMensaje
                    ? (isDark ? '#cbd5e1' : '#475569')
                    : (isDark ? '#64748b' : '#94a3b8'),
                fontStyle: ticket.vUltimoMensaje || ticket.vAsunto ? 'normal' : 'italic',
                fontWeight: isUnread ? 500 : 400
              }}
            >
              {ticket.vUltimoMensaje ? formatWhatsAppText(ticket.vUltimoMensaje, { inlineOnly: true }) : (ticket.vAsunto ? '' : 'Sin mensajes')}
            </span>
          </p>
        </div>

        {/* 3. Right Action & Code Col */}
        <div
          className="d-flex flex-column align-items-md-end justify-content-between flex-shrink-0 ps-md-3 border-start-md pt-2 pt-md-0 position-relative"
          style={{ minWidth: '140px', borderColor: isDark ? '#233142' : '#eef2f6' }}
        >
          {/* Indicador Sin Leer estilo WhatsApp en columna derecha */}
          {isUnread && (
            <div
              className="d-flex align-items-center gap-1 mb-2 px-2 py-1 rounded-pill"
              style={{
                backgroundColor: isDark ? 'rgba(16, 185, 129, 0.2)' : '#ecfdf5',
                color: isDark ? '#6ee7b7' : '#065f46',
                fontSize: '0.74rem',
                fontWeight: 700,
                border: isDark ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid #a7f3d0'
              }}
            >
              <span
                className="rounded-circle"
                style={{ width: '7px', height: '7px', backgroundColor: '#25d366', display: 'inline-block' }}
              />
              <span>Sin leer</span>
            </div>
          )}

          {/* Asesor Asignado (Solo informativo si ya tiene asesor) */}
          {assignedAgentName && (
            <div
              className="d-flex align-items-center justify-content-center gap-1 px-2 py-1 rounded-2 shadow-none w-100 mb-2"
              style={{
                backgroundColor: isDark ? 'rgba(5, 150, 105, 0.2)' : '#ecfdf5',
                color: isDark ? '#34d399' : '#065f46',
                border: isDark ? '1px solid rgba(52, 211, 153, 0.3)' : '1px solid #a7f3d0',
                fontSize: '0.80rem',
                fontWeight: 600,
                cursor: 'default'
              }}
              title={`Asesor asignado: ${assignedAgentName}`}
            >
              <FontAwesomeIcon icon={faUserCheck} style={{ fontSize: '0.75rem', color: isDark ? '#34d399' : '#059669' }} />
              <span className="text-truncate">{assignedAgentName}</span>
            </div>
          )}

          {/* Botón Ver Ticket */}
          <a
            href={ticketUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onSelectTicket(ticket);
            }}
            className="btn btn-sm text-white d-flex align-items-center justify-content-center gap-2 px-3 py-1 rounded-2 shadow-none mb-2 w-100"
            style={{
              backgroundColor: '#0886caff',
              fontSize: '0.85rem',
              fontWeight: 600,
              textDecoration: 'none'
            }}
          >
            <FontAwesomeIcon icon={faEye} style={{ fontSize: '0.8rem' }} />
            <span>Ver ticket</span>
          </a>
          {/* Botón Asignar Ticket: Solo en Pendientes y sin asesor */}
          {isPending && !hasAssignedAgent && (
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                if (onAssignTicketAgent) {
                  onAssignTicketAgent(ticket); // Dispara la apertura del modal con la lista de asesores
                }
              }}
              className="btn btn-sm text-white d-flex align-items-center justify-content-center gap-2 px-3 py-1 rounded-2 shadow-none mb-2 w-100"
              style={{
                backgroundColor: '#059669',
                fontSize: '0.85rem',
                fontWeight: 600,
                border: 'none'
              }}
            >
              <FontAwesomeIcon icon={faUserCheck} style={{ fontSize: '0.8rem' }} />
              <span>Asignar Ticket</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketCardItem;
